import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { FaPlus, FaSearch, FaTrash, FaPlusCircle, FaMinusCircle } from 'react-icons/fa';
import serviceService from '../services/serviceService';
import produtoService from '../services/produtoService';
import vendaService from '../services/vendaService';
import PageHeader from './PageHeader';
import './../pages/Dashboard.css';

const Caixa = () => {
  // --- ESTADOS ---
  const [todosServicos, setTodosServicos] = useState([]);
  const [todosProdutos, setTodosProdutos] = useState([]);
  const [comanda, setComanda] = useState([]);
  const [total, setTotal] = useState(0);
  const [valorPago, setValorPago] = useState('');
  const [troco, setTroco] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // --- EFEITOS ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [servicosData, produtosData] = await Promise.all([
          serviceService.getServices(),
          produtoService.getProdutos()
        ]);
        setTodosServicos(servicosData);
        setTodosProdutos(produtosData);
      } catch (error) {
        toast.error("Erro ao carregar serviços e produtos.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const novoTotal = comanda.reduce((acc, item) => acc + (item.preco_unitario * item.quantidade), 0);
    setTotal(novoTotal);
  }, [comanda]);

  useEffect(() => {
    const pago = parseFloat(valorPago) || 0;
    setTroco(pago > total ? pago - total : 0);
  }, [valorPago, total]);

  // --- FUNÇÕES ---
  const adicionarItem = (item, tipo) => {
    const itemExistente = comanda.find(i => i.id === item.id && i.tipo === tipo);
    if (itemExistente) {
      ajustarQuantidade(item.id, tipo, 1);
    } else {
      setComanda(prev => [...prev, {
        id: item.id, tipo: tipo,
        nome: tipo === 'servico' ? item.nome_servico : item.nome,
        imagem: item.imagem, quantidade: 1,
        preco_unitario: parseFloat(item.preco)
      }]);
    }
    toast.success(`${tipo === 'servico' ? item.nome_servico : item.nome} adicionado!`);
  };

  const ajustarQuantidade = (itemId, itemTipo, delta) => {
    setComanda(prev => prev.map(item =>
      (item.id === itemId && item.tipo === itemTipo) ? { ...item, quantidade: item.quantidade + delta } : item
    ).filter(item => item.quantidade > 0));
  };

  const finalizarVenda = (metodo_pagamento) => {
    if (comanda.length === 0) {
      toast.error("Adicione pelo menos um item à venda.");
      return;
    }
    const vendaData = {
      itens: comanda.map(({ id, tipo, quantidade, preco_unitario }) => ({ id, tipo, quantidade, preco_unitario })),
      metodo_pagamento,
    };
    toast.promise(vendaService.createVenda(vendaData), {
      loading: 'Registrando venda...',
      success: () => {
        setComanda([]);
        setValorPago('');
        return `Venda finalizada com ${metodo_pagamento}!`;
      },
      error: 'Falha ao registrar a venda.',
    });
  };

  const filteredServicos = todosServicos.filter(s => s.nome_servico.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredProdutos = todosProdutos.filter(p => p.nome.toLowerCase().includes(searchTerm.toLowerCase()));

  // --- JSX ---
  return (
    <div className="page-container">
      <PageHeader title="Caixa">
        <div className="search-bar">
          <FaSearch />
          <input type="text" placeholder="Buscar produto ou serviço..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </PageHeader>

      <div className="caixa-layout">
        {/* Coluna da Esquerda: Itens Disponíveis */}
        <div className="card caixa-items-list">
          {loading ? (
            <p>Carregando...</p>
          ) : (
            <>
              <h4>Serviços</h4>
              <div className="items-grid">
                {filteredServicos.map(s => (
                  <div key={s.id} className="item-card" onClick={() => adicionarItem(s, 'servico')}>
                     <img src={s.imagem || 'https://via.placeholder.com/150'} alt={s.nome_servico} />
                     <div className="item-card-info">
                       <h4>{s.nome_servico}</h4>
                       <p>R$ {s.preco}</p>
                     </div>
                     <button className="add-button"><FaPlus /></button>
                  </div>
                ))}
              </div>
              <h4>Produtos</h4>
              <div className="items-grid">
                {filteredProdutos.map(p => (
                  <div key={p.id} className="item-card" onClick={() => adicionarItem(p, 'produto')}>
                    <img src={p.imagem || 'https://via.placeholder.com/150'} alt={p.nome} />
                    <div className="item-card-info">
                      <h4>{p.nome}</h4>
                      <p>R$ {p.preco}</p>
                    </div>
                    <button className="add-button"><FaPlus /></button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Coluna da Direita: Comanda */}
        <div className="card caixa-comanda">
          <h3>Comanda</h3>
          <div className="comanda-lista">
            {comanda.length === 0 ? (
              <p className="comanda-vazia">Comanda vazia</p>
            ) : (
              comanda.map(item => (
                <div key={`${item.tipo}-${item.id}`} className="comanda-item">
                  <img src={item.imagem || 'https://via.placeholder.com/150'} alt={item.nome} />
                  <div className="comanda-item-info">
                    <strong>{item.nome}</strong>
                    <span>R$ {item.preco_unitario.toFixed(2)}</span>
                  </div>
                  <div className="quantity-controls">
                    <button onClick={() => ajustarQuantidade(item.id, item.tipo, -1)}><FaMinusCircle /></button>
                    <span>{item.quantidade}</span>
                    <button onClick={() => ajustarQuantidade(item.id, item.tipo, 1)}><FaPlusCircle /></button>
                  </div>
                  <div className="comanda-item-total">
                    <strong>R$ {(item.preco_unitario * item.quantidade).toFixed(2)}</strong>
                  </div>
                  <button className="remove-button" onClick={() => ajustarQuantidade(item.id, item.tipo, -item.quantidade)}><FaTrash /></button>
                </div>
              ))
            )}
          </div>
          <div className="caixa-summary">
            <div className="total-display">
              <span>Total</span>
              <strong>R$ {total.toFixed(2)}</strong>
            </div>
            <div className="form-group">
              <label>Valor Pago</label>
              <input type="number" className="form-input" placeholder="R$ 0,00" value={valorPago} onChange={e => setValorPago(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Troco</label>
              <input type="text" className="form-input troco-input" readOnly value={`R$ ${troco.toFixed(2)}`} />
            </div>
            <button className="btn btn-primary" style={{width: '100%'}} onClick={() => finalizarVenda(valorPago > total ? 'Dinheiro' : 'Pix/Cartão')}>
              Finalizar Venda
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Caixa;